/**
 * Forma comum dos processos de tracking manual do Backoffice (spec B):
 * onboarding, migração de conta, transferência de custódia, atualização de
 * preço médio, cobrança de taxas e cadastro de profissional. Todos guardam
 * etapas, pendências e instituição — a UI compartilhada (process-steps-
 * timeline, pending-issues-list, tracking-status-history — Wave 4-base)
 * renderiza a partir desse shape, independente do recurso.
 *
 * @typedef {Object} TrackingStep
 * @property {string} id
 * @property {string} label
 * @property {string} status - um dos valores de TRACKING_STATUS
 * @property {string|null} [data] - data de conclusão da etapa (ISO/DD-MM-YYYY conforme aawzDate)
 *
 * @typedef {Object} TrackingPendencia
 * @property {string} id
 * @property {string} motivo - texto legível do que falta (ex: "comprovante de residência pendente")
 * @property {boolean} resolvida
 *
 * @typedef {Object} TrackingProcess
 * @property {number|string} id
 * @property {string} status - um dos valores de TRACKING_STATUS
 * @property {TrackingStep[]} etapas
 * @property {TrackingPendencia[]} pendencias
 * @property {string|null} previsao_conclusao
 * @property {string} instituicao - ver BROKERS/MAP_BROKERS de ws/constants/Brokers
 * @property {string} [tipo] - subtipo do processo (ex: 'PF' | 'PJ' no onboarding)
 */

export const TRACKING_STATUS = {
  EM_ANDAMENTO: 'em_andamento',
  PENDENTE: 'pendente',
  CONCLUIDO: 'concluido',
  CANCELADO: 'cancelado',
}

// B2 — Onboarding: convite (PF), abertura assistida via mesa, ou migração.
export const ETAPAS_ONBOARDING = [
  { id: 'convite_enviado', label: 'Convite enviado' },
  { id: 'aguardando_confirmacao', label: 'Aguardando confirmação do cliente' },
  { id: 'documentos_pendentes', label: 'Documentos pendentes' },
  { id: 'em_compliance', label: 'Em compliance' },
  { id: 'conta_aberta', label: 'Conta aberta' },
]

// B2 — Migração de conta: cliente já tem conta no banco, push para aceitar
// entrar no guarda-chuva do escritório.
export const ETAPAS_MIGRACAO = [
  { id: 'notificar_assessores', label: 'Notificar assessores' },
  { id: 'configurando_migracao', label: 'Configurando migração' },
  { id: 'aguardando_aceite_cliente', label: 'Aguardando aceite do cliente' },
  { id: 'migracao_concluida', label: 'Migração concluída' },
]

// B4 — Transferência de custódia: entrada/saída, por ativo ou totalidade.
export const ETAPAS_CUSTODIA = [
  { id: 'solicitacao_enviada', label: 'Solicitação enviada' },
  { id: 'em_analise_origem', label: 'Em análise pela instituição de origem' },
  { id: 'transferencia_autorizada', label: 'Transferência autorizada' },
  { id: 'ativos_recebidos', label: 'Ativos recebidos' },
]

// B6 — Cadastro do profissional na corretora.
export const ETAPAS_PROFISSIONAL = [
  { id: 'dados_basicos', label: 'Dados básicos' },
  { id: 'em_compliance', label: 'Em compliance' },
  { id: 'documentos_pendentes', label: 'Pendências de documentos' },
  { id: 'contrato_master', label: 'Contrato para o master assinar' },
  { id: 'boas_vindas', label: 'E-mail de boas-vindas enviado' },
]

// Wave B (groundwork v1.2) — Troca de titularidade: cliente migra de
// carteira/profissional dentro do mesmo escritório, com aprovação do gestor
// antes de efetivar.
export const ETAPAS_TROCA_TITULARIDADE = [
  { id: 'solicitada', label: 'Solicitada' },
  { id: 'aprovacao_gestor', label: 'Aprovação do gestor' },
  { id: 'efetivada', label: 'Efetivada' },
]

/** Mapa processo → etapas padrão, usado para popular um TrackingProcess novo. */
export const TRACKING_ETAPAS_BY_PROCESS = {
  onboarding: ETAPAS_ONBOARDING,
  migracao: ETAPAS_MIGRACAO,
  custodia: ETAPAS_CUSTODIA,
  profissional: ETAPAS_PROFISSIONAL,
  troca_titularidade: ETAPAS_TROCA_TITULARIDADE,
}

// Rótulo em PT-BR do `status` de um TrackingProcess (nível processo, não
// etapa) — mesma forma de `CONCILIACAO_STATUS_LABEL` (Conciliation.js).
// "Pendente" vira "Com pendência" aqui de propósito: no nível do processo
// esse status representa "há uma pendência bloqueando o avanço", não
// "etapa futura ainda não iniciada" (ambiguidade só existe no nível etapa,
// tratada localmente em `process-steps-timeline.vue`).
export const TRACKING_STATUS_LABEL = {
  [TRACKING_STATUS.EM_ANDAMENTO]: 'Em andamento',
  [TRACKING_STATUS.PENDENTE]: 'Com pendência',
  [TRACKING_STATUS.CONCLUIDO]: 'Concluído',
  [TRACKING_STATUS.CANCELADO]: 'Cancelado',
}

export const TRACKING_STATUS_COLOR = {
  [TRACKING_STATUS.EM_ANDAMENTO]: '#6A7086',
  [TRACKING_STATUS.PENDENTE]: '#E0AF00',
  [TRACKING_STATUS.CONCLUIDO]: '#56AF79',
  [TRACKING_STATUS.CANCELADO]: '#EB5757',
}

export const TRACKING_STATUS_ICON = {
  [TRACKING_STATUS.EM_ANDAMENTO]: 'clock',
  [TRACKING_STATUS.PENDENTE]: 'warning-circle',
  [TRACKING_STATUS.CONCLUIDO]: 'check-circle',
  [TRACKING_STATUS.CANCELADO]: 'close-circle',
}

const FALLBACK_TRACKING_STATUS_COLOR = '#8E8E8E'
const FALLBACK_TRACKING_STATUS_ICON = 'info-circle'

/**
 * Resolve label/cor/ícone de um `status` de processo (nível TrackingProcess,
 * não etapa), com fallback neutro para status ausente/desconhecido — usado
 * por `process-summary-cards.vue` e por qualquer badge de status de processo
 * nas telas 4A/4B/4C (evita duplicar o mapa em cada view).
 * @param {String} status Uma das chaves de TRACKING_STATUS.
 * @returns {{label: String, color: String, icon: String}}
 */
export function getTrackingStatusInfo(status) {
  const key = status || TRACKING_STATUS.EM_ANDAMENTO
  return {
    label: TRACKING_STATUS_LABEL[key] || key,
    color: TRACKING_STATUS_COLOR[key] || FALLBACK_TRACKING_STATUS_COLOR,
    icon: TRACKING_STATUS_ICON[key] || FALLBACK_TRACKING_STATUS_ICON,
  }
}

/** Opções prontas para o filtro `input-by-type type="autocomplete"` da view (ex: `view-professionals.vue`). */
export const TRACKING_STATUS_FILTER_OPTIONS = [
  { text: 'Todos', value: null },
  { text: TRACKING_STATUS_LABEL[TRACKING_STATUS.EM_ANDAMENTO], value: TRACKING_STATUS.EM_ANDAMENTO },
  { text: TRACKING_STATUS_LABEL[TRACKING_STATUS.PENDENTE], value: TRACKING_STATUS.PENDENTE },
  { text: TRACKING_STATUS_LABEL[TRACKING_STATUS.CONCLUIDO], value: TRACKING_STATUS.CONCLUIDO },
  { text: TRACKING_STATUS_LABEL[TRACKING_STATUS.CANCELADO], value: TRACKING_STATUS.CANCELADO },
]
